/**
 * @fileoverview Virtual Race Activity Selector and Login Components
 * @requires virtual-race-utility.js
 */

(function() {
    'use strict';

    if (typeof window.VirtualRaceUtils === 'undefined') {
        console.error('VirtualRaceUtils not loaded');
        return;
    }

    const { SecurityUtils, StorageManager, UrlParams, TimeBasedGreeting, BaseCustomElement } = window.VirtualRaceUtils;

    // ============================================================================
    // ACTIVITY SELECTOR CUSTOM ELEMENT
    // ============================================================================

    class VirtualRaceActivitySelector extends BaseCustomElement {
        constructor() {
            super();
            this._userActivities = [];
            this._userData = {};
            this._selectedActivity = null;
            this._loadingActivities = false;
            this._activityType = '';
            this._userId = '';

            // Activity type configuration
            this._activityConfig = {
                fitbit: { name: 'Fitbit', days: 60 },
                garmin: { name: 'Garmin', days: 30 },
                strava: { name: 'Strava', days: 60 }
            };
        }

        async _init() {
            this._setupEventListeners();
            await this._fetchActivities();
        }

        _setupEventListeners() {
            // Delegated click handler for activity selection
            this._addEventListener(this, 'click', this._handleClick.bind(this));
        }

        _handleClick(e) {
            const activityItem = e.target.closest('[data-useractivity]');
            const submitBtn = e.target.closest('[data-submit-activity]');
            const loadMoreBtn = e.target.closest('[data-loadmoreactivity]');
            const disconnectBtn = e.target.closest('[data-disconnect]');

            if (activityItem) {
                e.preventDefault();
                const activityData = activityItem.dataset.selectedactivity;
                if (activityData) {
                    this._selectActivity(JSON.parse(activityData));
                }
            } else if (submitBtn && !submitBtn.classList.contains('disabled')) {
                e.preventDefault();
                this._submitActivity(submitBtn);
            } else if (loadMoreBtn) {
                e.preventDefault();
                this._loadMoreActivities(loadMoreBtn);
            } else if (disconnectBtn) {
                e.preventDefault();
                const type = disconnectBtn.dataset.type;
                this._revokeConnection(type);
            }
        }

        _getActivityType() {
            if (!this._activityType) {
                this._activityType = UrlParams.get('type') || '';
            }
            return this._activityType;
        }

        _getUserId() {
            if (!this._userId) {
                this._userId = UrlParams.get('user_id') || '';
            }
            return this._userId;
        }

        _getNoActivitiesMessage() {
            const type = this._getActivityType();
            const config = this._activityConfig[type];
            if (config) {
                return `We couldn't find any activities recorded on ${config.name} from the past ${config.days} days`;
            }
            return 'We couldn\'t find any activities recorded.';
        }

        _shouldShowDisconnectButton() {
            const type = this._getActivityType();
            const config = this._activityConfig[type];
            if (type === 'garmin') {
                const userId = this._getUserId();
                if (!userId || userId.trim() === '') {
                    return false;
                }
            }

            return config ? config.showDisconnect : false;
        }

        _getGarminMessage() {
            const type = this._getActivityType();
            if (type === 'garmin') {
                const userId = this._getUserId();
                if (!userId || userId.trim() === '') {
                    return 'Please connect your Garmin account to view activities.';
                }
            }
            return '';
        }

        async _fetchActivities() {
            const userId = this._getUserId();
            const authType = this._getActivityType();
            const connectFlow = this._$('[data-connectflow]');
            const otherFlow = this._$('[data-otherFlow]');

            if (authType === 'strava' || authType === 'fitbit' || authType === 'garmin') {
                if (connectFlow) connectFlow.classList.remove('d-none');
                if (otherFlow) otherFlow.classList.add('d-none');
            } else {
                if (connectFlow) connectFlow.classList.add('d-none');
                if (otherFlow) otherFlow.classList.remove('d-none');
                return;
            }

            const payload = {
                user_id: userId,
                per_page: '10',
                page: '1',
                type: authType,
                date: null
            };

            this._loadingActivities = true;
            this._render();

            // Simulate delay for better UX
            await new Promise(resolve => setTimeout(resolve, 1000));

            await this._callActivitiesAPI(payload);
        }

        async _callActivitiesAPI(payload) {
            try {
                const response = await fetch(`/apps/proxy/oauth/athlete/activities`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const res = await response.json();
                const loadMoreBtn = this._$('[data-loadmoreactivity]');
                if (loadMoreBtn) {
                    if (res.activities.length <= 0) {
                        loadMoreBtn.classList.add('d-none');
                    } else {
                        loadMoreBtn.classList.remove('d-none');
                    }
                }
                const newActivities = res.activities.map(item => ({
                    ...item,
                    selected: false
                }));
                this._userActivities = [...this._userActivities, ...newActivities];
                if (this._userActivities.length > 0) {
                    this._userActivities.sort((a, b) => {
                        const dateA = a.date ? new Date(a.date) : new Date(0);
                        const dateB = b.date ? new Date(b.date) : new Date(0);
                        return dateB - dateA;
                    });
                }

                this._userData = res.userData;
            } catch (error) {
                console.error("Error fetching activities:", error);
            } finally {
                this._loadingActivities = false;
                this._render();
            }
        }

        async _loadMoreActivities(btn) {
            this._loadingActivities = true;
            this._render();

            const nextPage = parseInt(btn.dataset.index || '1') + 1;
            btn.dataset.index = nextPage;

            const payload = {
                user_id: this._getUserId(),
                per_page: '10',
                page: String(nextPage),
                type: this._getActivityType(),
                date: null
            };

            await this._callActivitiesAPI(payload);
        }

        _selectActivity(activity) {
            this._selectedActivity = activity;
            StorageManager.setItem('selectedActivity', activity);
            StorageManager.setItem('userData', this._userData);
            StorageManager.setItem('activityType', this._getActivityType());
            const submitBtn = this._$('[data-submit-activity]');
            if (submitBtn) {
                submitBtn.classList.remove('disabled');
            }

            // Update selected state in UI
            this._$$('[data-selectedactivity]').forEach(item => {
                item.classList.remove('selected');
            });

            const selectedEl = this.querySelector(`[data-selectedactivity='${JSON.stringify(activity)}']`);
            if (selectedEl) {
                selectedEl.classList.add('selected');
            }
        }

        async _submitActivity(btn) {
            btn.classList.add('loading');
            if (localStorage.getItem('activityType') === 'garmin') {
                try {
                    await this._revokeConnection('garmin', false);
                    window.location.href = '/pages/enter-your-virtual-race-time?logged=true';
                } catch (error) {
                    window.location.href = '/pages/enter-your-virtual-race-time?logged=true';
                } finally {
                    btn.classList.remove('loading');
                }
                return;
            }

            window.location.href = '/pages/enter-your-virtual-race-time?logged=true';
            btn.classList.remove('loading');
        }

        async _revokeConnection(type, shouldRedirect = true) {
            const userId = parseInt(window.user_id);
            if (!userId) {
                throw new Error('No user_id found');
            }

            try {
                const response = await fetch(`/apps/proxy/oauth/revoke/${type}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ user_id: userId })
                });

                if (response.status === 500 || response.status === 404) {
                    const responseMessage = await response.text();
                    if (responseMessage.includes("Access token not found")) {
                        window.location.href = '/pages/enter-your-virtual-race-time';
                    }
                    return response;
                }

                if (response.ok && shouldRedirect) {
                    window.location.href = '/pages/enter-your-virtual-race-time';
                }

                return response;
            } catch (error) {
                console.error('Error revoking connection:', error);
                throw error;
            }
        }

        _render() {
            const activitiesList = this._$('[data-activities-list]');
            if (!activitiesList) return;

            // Clear existing content
            activitiesList.innerHTML = '';
            if (this._loadingActivities) {
                activitiesList.innerHTML = `
                    <li class="text-center p-4">
                        <div>
                            <div class="spinner-border text-primary mb-3" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </div>
                            <p class="text-muted h4">Loading your activities...</p>
                        </div>
                    </li>
                `;
                return;
            }
            if (this._userActivities && this._userActivities.length > 0) {
                const heading = this._$('[data-heading]');
                if (heading) heading.classList.remove('d-none');

                this._userActivities.forEach(activity => {
                    const li = document.createElement('li');
                    li.innerHTML = `
                        <div class="strava-race-option"
                             data-useractivity
                             data-selectedactivity='${JSON.stringify(activity)}'>
                            <div class="strava-race-name">
                                <p>${SecurityUtils.sanitizeText(activity.name || '')}</p>
                            </div>
                            <div class="strava-race-divider"><p></p></div>
                            <div class="strava-race-info">
                                <p>${SecurityUtils.sanitizeText(activity.date || '')}</p>
                            </div>
                            <div class="strava-race-info">
                                <p>${activity.distance} mile${activity.distance == '1' ? '' : 's'}</p>
                            </div>
                        </div>
                    `;
                    activitiesList.appendChild(li);
                });
            } else {
                const garminMsg = this._getGarminMessage();
                const activityType = this._getActivityType();
                const config = this._activityConfig[activityType];
                const activityName = config ? config.name : activityType;

                activitiesList.innerHTML = `
                    <li class="text-center p-4">
                        <div>
                            <p class="text-muted h4 mb-5">${this._getNoActivitiesMessage()}</p>
                            <p class="fs-xl mb-3">Would you like to enter your time <a href="/pages/enter-your-virtual-race-time" class="text-decoration-underline text-primary">manually?</a></p>
                            ${(activityType === 'fitbit' || activityType === 'garmin') ? `
                                <a href="#" class="btn btn-outline-primary mt-3" data-disconnect data-type="${activityType}">
                                    Disconnect ${activityName}
                                </a>
                            ` : ''}
                            ${garminMsg ? `<p class="text-muted mt-3">${garminMsg}</p>` : ''}
                        </div>
                    </li>
                `;
            }
        }
    }

    // ============================================================================
    // PRODUCT IMAGE CUSTOM ELEMENT
    // ============================================================================

    class VirtualRaceProductImage extends BaseCustomElement {
        constructor() {
            super();
            this._isLoading = true;
            this._imageDetail = {};
        }

        async _init() {
            await this._fetchProductImage();
        }

        async _fetchProductImage() {
            const selectedProduct = StorageManager.getSelectedProductId();
            if (!selectedProduct) {
                this._isLoading = false;
                this._render();
                return;
            }

            const query = `
                query productQuery {
                    product(id: "gid://shopify/Product/${selectedProduct}") {
                        featuredImage {
                            altText
                            id
                            originalSrc
                            src
                            url
                            width
                            height
                        }
                    }
                }
            `;

            try {
                const response = await fetch(`${window.globalSpace.apiDomain}/api/2023-01/graphql.json`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Shopify-Storefront-Access-Token": "382d0c774e95be1f0d7c90e6c08bba49"
                    },
                    body: JSON.stringify({ query })
                });

                const res = await response.json();

                if (res.data?.product?.featuredImage) {
                    const img = res.data.product.featuredImage;
                    this._imageDetail = {
                        imgSrc: img.originalSrc,
                        alt: img.altText,
                        width: img.width,
                        height: img.height
                    };
                }
            } catch (error) {
                console.error("Error fetching product image:", error);
            } finally {
                this._isLoading = false;
                this._render();
            }
        }

        _render() {
            const placeholder = this._$('[data-placeholder]');
            const image = this._$('[data-product-image]');

            if (this._isLoading) {
                if (placeholder) placeholder.classList.remove('d-none');
                if (image) image.classList.add('d-none');
            } else {
                if (placeholder) placeholder.classList.add('d-none');
                if (image) {
                    image.classList.remove('d-none');
                    if (this._imageDetail.imgSrc) {
                        image.src = `${this._imageDetail.imgSrc}?width=400`;
                        image.alt = this._imageDetail.alt || '';
                        image.width = this._imageDetail.width || 400;
                        image.height = this._imageDetail.height || 400;
                    }
                }
            }
        }
    }
    if (!customElements.get('virtual-race-activity-selector')) {
        customElements.define('virtual-race-activity-selector', VirtualRaceActivitySelector);
    }
    if (!customElements.get('virtual-race-product-image')) {
        customElements.define('virtual-race-product-image', VirtualRaceProductImage);
    }

    class RaceStatsLoginController {
        init() {
            this._initLoginPageUI();
            this._initConnection();
        }

        _initLoginPageUI() {
            const greetingEl = document.querySelector('[data-greeting]');
            if (greetingEl && TimeBasedGreeting) {
                greetingEl.textContent = TimeBasedGreeting.getGreeting();
            }
            const productId = UrlParams.getVrId();
            const baseEnterUrl = '/pages/enter-your-virtual-race-time';
            const enterUrlWithVr = productId ? baseEnterUrl + '?vrId=' + productId : baseEnterUrl;

            const buttonVrLogin = document.querySelectorAll('[data-button-vrlogin]');
            buttonVrLogin.forEach(button => {
                button.setAttribute('href', enterUrlWithVr);
            });

            const returnToInput = document.querySelector('input[name="return_to"]');
            if (returnToInput) {
                returnToInput.setAttribute('value', enterUrlWithVr);
                returnToInput.value = enterUrlWithVr;
            }

            if (productId) {
                const signupUrl = '/account/register?return_to=' + encodeURIComponent(enterUrlWithVr);
                document.querySelectorAll('[data-button-vrsignup]').forEach(button => {
                    button.setAttribute('href', signupUrl);
                });

                try {
                    if (enterUrlWithVr.charAt(0) === '/' && enterUrlWithVr.charAt(1) !== '/' && enterUrlWithVr.indexOf('://') === -1) {
                        sessionStorage.setItem('vrPostAuthReturn', JSON.stringify({ url: enterUrlWithVr, ts: Date.now() }));
                    }
                } catch (e) {}
            }
        }

        _initConnection() {
            const loginButton = document.querySelector('.block__form-login');
            const email = document.querySelector('#gfr-email');
            const password = document.querySelector('#gfe-password');

            if (!loginButton || !email || !password) return;

            const checkInputs = () => {
                if (email.value.trim() !== '' && password.value.trim() !== '') {
                    loginButton.removeAttribute('disabled');
                } else {
                    loginButton.setAttribute('disabled', 'disabled');
                }
            };

            email.addEventListener('input', checkInputs);
            password.addEventListener('input', checkInputs);
        }
    }

    const racestatsLogin = new RaceStatsLoginController();
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => racestatsLogin.init());
    } else {
        racestatsLogin.init();
    }
})();
